import React, { useState, useEffect } from "react";
import { css } from "@emotion/css";
import { FaWhatsapp, FaCheck } from "react-icons/fa";

const styles = {
    // Success Dialog Styles
    successDialogOverlay: css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        padding: 1rem;
    `,
    successDialog: css`
        background: white;
        border-radius: 12px;
        padding: 2rem;
        max-width: 80%;
        width: 100%;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04);
        text-align: center;
        max-height: 90vh;
        overflow-y: auto;

        @media (min-width: 768px) {
            max-width: 700px;
        }
    `,
    successDialogHeader: css`
        margin-bottom: 1.5rem;
        h2 {
            color: #059669;
            font-size: 1.5rem;
            font-weight: 700;
            margin: 0;
        }
    `,
    successDialogContent: css`
        p {
            color: #374151;
            margin-bottom: 1.5rem;
            font-size: 1rem;
            line-height: 1.6;
        }
    `,
    successInfo: css`
        background: #f3f4f6;
        border-radius: 8px;
        padding: 1rem;
        margin-bottom: 1.5rem;
        text-align: left;

        .info-item {
            margin-bottom: 0.5rem;
            font-size: 0.9rem;

            &:last-child {
                margin-bottom: 0;
            }

            strong {
                color: #1f2937;
            }
        }
    `,
    whatsappSection: css`
        background: #ecfdf5;
        border: 1px solid #a7f3d0;
        border-radius: 8px;
        padding: 1.5rem;
        margin-bottom: 1.5rem;

        p {
            margin-bottom: 1.5rem;
            color: #065f46;
            font-weight: 700;
            font-size: 1.1rem;
        }
    `,
    whatsappButton: css`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        background-color: #25d366;
        color: white;
        text-decoration: none;
        padding: 1rem;
        margin-bottom: 1rem;
        border-radius: 8px;
        font-weight: 600;
        font-size: 1rem;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
        transition: all 0.2s ease;
        border: 1px solid #128c7e;
        position: relative;
        overflow: hidden;

        &:hover {
            background-color: #128c7e;
            transform: translateY(-1px);
            box-shadow: 0 6px 8px -1px rgba(0, 0, 0, 0.15);
        }

        &:active {
            transform: translateY(0);
        }

        &:last-child {
            margin-bottom: 0;
        }
    `,
    whatsappButtonClicked: css`
        background-color: #059669 !important;
        border-color: #047857 !important;
        opacity: 0.9;

        &::after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 255, 0, 0.1);
            pointer-events: none;
        }
    `,
    successDialogActions: css`
        margin-top: 1.5rem;
    `,
    okButton: css`
        background: #3b82f6;
        color: white;
        border: none;
        padding: 0.75rem 2rem;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover:not(:disabled) {
            background: #2563eb;
        }

        &:disabled {
            background: #9ca3af;
            cursor: not-allowed;
            opacity: 0.7;
        }

        &:focus {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
        }
    `,
};

export const SuccessDialog = ({
    isOpen,
    onClose,
    responseData,
    formData,
    onGoHome,
}) => {
    const [clickedLinks, setClickedLinks] = useState(new Set());

    useEffect(() => {
        if (isOpen) {
            setClickedLinks(new Set());
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const whatsappGroupsList = Object.values(
        responseData?.whatsapp_groups ?? {}
    ).filter((value) => value !== null);

    const handleLinkClick = (index) => {
        setClickedLinks((prev) => {
            const newSet = new Set(prev);
            newSet.add(index);
            return newSet;
        });
    };

    const allLinksClicked =
        whatsappGroupsList.length > 0 &&
        whatsappGroupsList.length === clickedLinks.size;

    return (
        <div className={styles.successDialogOverlay}>
            <div className={styles.successDialog}>
                <div className={styles.successDialogHeader}>
                    <h2>Registration Successful!</h2>
                </div>
                <div className={styles.successDialogContent}>
                    <p>
                        {responseData?.message || "Welcome to Club Mango! Your registration has been completed successfully."}
                    </p>

                    <div className={styles.successInfo}>
                        <div className="info-item">
                            <strong>
                                Your User ID is: {responseData.userId}
                            </strong>
                        </div>
                    </div>

                    {whatsappGroupsList.length > 0 && (
                        <div className={styles.whatsappSection}>
                            {whatsappGroupsList.map(
                                ({ link, message }, index) => (
                                    <a
                                        key={index}
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`${styles.whatsappButton} ${clickedLinks.has(index)
                                            ? styles.whatsappButtonClicked
                                            : ""
                                            }`}
                                        onClick={() => handleLinkClick(index)}
                                    >
                                        {clickedLinks.has(index) ? (
                                            <FaCheck
                                                className={css`
                                                    margin-right: 0.5rem;
                                                    font-size: 1.2rem;
                                                `}
                                            />
                                        ) : (
                                            <FaWhatsapp
                                                className={css`
                                                    margin-right: 0.5rem;
                                                    font-size: 1.2rem;
                                                `}
                                            />
                                        )}
                                        {message}
                                        {clickedLinks.has(index) && " (Joined)"}
                                    </a>
                                )
                            )}
                        </div>
                    )}

                    <div className={styles.successDialogActions}>
                        <button
                            onClick={onGoHome}
                            className={styles.okButton}
                        >
                            OK
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
